/**
 * Preventive maintenance (piece 28; decision 0720). A `PMSchedule` is
 * triggered by the calendar or by a meter, never both on one row, and
 * `checkPMSchedules` generates one Draft Work Order per schedule per due
 * period. 🔨 The period last generated lives on the schedule, and the run
 * compares against it before it writes, so a re-run makes nothing.
 */
import { DAY_MS } from '../zonedTime';
export const PM_TRIGGER_TYPES = ['Calendar', 'Meter'] as const;
export type PMTriggerType = (typeof PM_TRIGGER_TYPES)[number];

export const CHECK_PM_SCHEDULES_JOB = 'checkPMSchedules';
/** Decision 0720: the hour key the job registers under, a `SettingValue` row seeded at 5. */
export const CHECK_PM_SCHEDULES_HOUR_KEY = 'assets.checkPMSchedulesRunHour';
export const CHECK_PM_SCHEDULES_DEFAULT_HOUR = 5;


/** A calendar day as its UTC midnight, the way a `@db.Date` column carries it. */
export function dayOnly(value: Date): Date {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
}

export function addPlanDays(day: Date, days: number): Date {
  return new Date(dayOnly(day).getTime() + days * DAY_MS);
}

function wholeDaysBetween(from: Date, to: Date): number {
  return Math.floor((dayOnly(to).getTime() - dayOnly(from).getTime()) / DAY_MS);
}

/**
 * The current due period of a Calendar schedule: the latest due date on or
 * before today, where due dates fall every `intervalDays` after `startsOn`.
 * Null before the first interval has passed.
 */
export function calendarPeriodOn(startsOn: Date, intervalDays: number, today: Date): Date | null {
  if (!Number.isInteger(intervalDays) || intervalDays <= 0) return null;
  const elapsed = wholeDaysBetween(startsOn, today);
  if (elapsed < intervalDays) return null;
  return addPlanDays(startsOn, Math.floor(elapsed / intervalDays) * intervalDays);
}

/** The next due date strictly after today, which the schedule's row shows as Next due. */
export function nextCalendarDueOn(startsOn: Date, intervalDays: number, today: Date): Date | null {
  if (!Number.isInteger(intervalDays) || intervalDays <= 0) return null;
  const elapsed = Math.max(0, wholeDaysBetween(startsOn, today));
  return addPlanDays(startsOn, (Math.floor(elapsed / intervalDays) + 1) * intervalDays);
}

/**
 * Where the schedule's last generated Work Order stands (decision 2560): open
 * while it is still to do, done once it reached Complete or Closed, void when
 * it was voided, and null before the first Draft.
 */
export type PMGeneratedState = 'open' | 'done' | 'void' | null;

/** A Work Order status read as done, still to do, or voided, for the schedule that generated it. */
export function pmGeneratedState(status: string | null | undefined): PMGeneratedState {
  if (!status) return null;
  if (status === 'Complete' || status === 'Closed') return 'done';
  if (status === 'Void') return 'void';
  return 'open';
}

export interface CalendarFacts {
  startsOn: Date;
  intervalDays: number;
  lastGeneratedDueOn: Date | null;
  /** The day the newest completed Work Order the schedule generated was performed, or null before one completed (decision 2560). */
  lastCompletedOn?: Date | null;
  /** Where the last generated Work Order stands (decision 2560). */
  lastGenerated?: PMGeneratedState;
}

/**
 * 🔒 The day a Calendar schedule counts from (decision 2560, amending 0720):
 * the later of `startsOn` and the day its newest completed Work Order was
 * performed. A person who re-dates Counting from past the last completion moves
 * the count; a completion after it moves the count again.
 */
export function calendarAnchor(facts: Pick<CalendarFacts, 'startsOn' | 'lastCompletedOn'>): Date {
  const starts = dayOnly(facts.startsOn);
  if (!facts.lastCompletedOn) return starts;
  const done = dayOnly(facts.lastCompletedOn);
  return done.getTime() > starts.getTime() ? done : starts;
}

export interface MeterFacts {
  meterThreshold: number;
  lastGeneratedAtMeterValue: number | null;
  /** The live reading in the family's base unit, or null where the node carries none. */
  reading: number | null;
}

export type PMDue =
  | { due: true; trigger: 'Calendar'; dueOn: Date; periodKey: string }
  | { due: true; trigger: 'Meter'; reading: number; periodKey: string }
  | { due: false; reason: 'notYet' | 'alreadyGenerated' | 'noReading' | 'inactive' };

/** 🔨 The idempotency key's second half, as text: the due date on a Calendar row, the reading on a Meter row. */
export function pmPeriodKey(due: { trigger: 'Calendar'; dueOn: Date } | { trigger: 'Meter'; reading: number }): string {
  return due.trigger === 'Calendar' ? `calendar:${dayOnly(due.dueOn).toISOString().slice(0, 10)}` : `meter:${due.reading}`;
}

/**
 * Whether a Calendar schedule is due today (decisions 0720 and 2560). The
 * periods fall every `intervalDays` after the anchor, and the current one is the
 * latest on or before today. 🔨 A schedule whose last Draft is still open
 * generates no other: that Draft is the visit still owed. A period already
 * generated is not generated again unless the Work Order it raised was done.
 */
export function calendarDue(facts: CalendarFacts, today: Date): PMDue {
  if (facts.lastGenerated === 'open') return { due: false, reason: 'alreadyGenerated' };
  const dueOn = calendarPeriodOn(calendarAnchor(facts), facts.intervalDays, today);
  if (!dueOn) return { due: false, reason: 'notYet' };
  // 🔨 A schedule already generated in the current due period generates none, however many runs ask.
  if (facts.lastGenerated !== 'done' && facts.lastGeneratedDueOn && dayOnly(facts.lastGeneratedDueOn).getTime() >= dueOn.getTime()) return { due: false, reason: 'alreadyGenerated' };
  return { due: true, trigger: 'Calendar', dueOn, periodKey: pmPeriodKey({ trigger: 'Calendar', dueOn }) };
}

/**
 * The Next due a Calendar row shows (decision 2560): the open Draft's period
 * while one is owed, the current period while it waits for tonight's run, else
 * the next period after today counted from the anchor.
 */
export function calendarNextDueOn(facts: CalendarFacts, today: Date): Date | null {
  if (facts.lastGenerated === 'open' && facts.lastGeneratedDueOn) return dayOnly(facts.lastGeneratedDueOn);
  const due = calendarDue(facts, today);
  if (due.due && due.trigger === 'Calendar') return due.dueOn;
  return nextCalendarDueOn(calendarAnchor(facts), facts.intervalDays, today);
}

/** 🔒 Overdue: the next due has passed and nothing since has completed it (decision 2560). Due today is not yet overdue. */
export function pmOverdue(nextDueOn: Date | null, today: Date): boolean {
  return nextDueOn !== null && dayOnly(nextDueOn).getTime() < dayOnly(today).getTime();
}

/**
 * The Meter rule (decision 0720, the open issue's proposal built): due once the
 * reading has risen by `meterThreshold` past the reading the last Draft was
 * generated at, zero before the first. A reading that jumps several thresholds
 * at once generates one Draft, and a reading that falls — a meter replaced —
 * waits until it climbs past the last generation again.
 */
export function meterDue(facts: MeterFacts): PMDue {
  if (facts.reading === null || !Number.isFinite(facts.reading)) return { due: false, reason: 'noReading' };
  const since = facts.lastGeneratedAtMeterValue ?? 0;
  if (facts.reading - since < facts.meterThreshold) return { due: false, reason: facts.lastGeneratedAtMeterValue === null ? 'notYet' : 'alreadyGenerated' };
  return { due: true, trigger: 'Meter', reading: facts.reading, periodKey: pmPeriodKey({ trigger: 'Meter', reading: facts.reading }) };
}

export interface ScheduleShape {
  triggerType: PMTriggerType;
  intervalDays?: number | null;
  startsOn?: Date | null;
  meterAttributeTypeId?: string | null;
  meterThreshold?: number | null;
  checklistTemplateId?: string | null;
  name?: string | null;
}

/** Calendar or Meter, never both on one row; a dual-triggered requirement is two rows. */
export function scheduleShapeProblem(input: ScheduleShape): string | null {
  if (!input.name || !input.name.trim()) return 'a maintenance schedule has a name';
  if (!input.checklistTemplateId) return 'a maintenance schedule names the checklist template its Work Order instantiates';
  if (input.triggerType === 'Calendar') {
    if (!input.intervalDays || !Number.isInteger(input.intervalDays) || input.intervalDays <= 0) return 'a calendar schedule repeats every whole number of days above zero';
    if (!input.startsOn) return 'a calendar schedule names the day its interval counts from';
    if (input.meterAttributeTypeId || input.meterThreshold) return 'a schedule is triggered by the calendar or by a meter, never both; make a second schedule for the other';
    return null;
  }
  if (input.triggerType === 'Meter') {
    if (!input.meterAttributeTypeId) return 'a meter schedule names the attribute it reads';
    if (input.meterThreshold === null || input.meterThreshold === undefined || !(input.meterThreshold > 0)) return 'a meter schedule is due after a rise above zero';
    if (input.intervalDays || input.startsOn) return 'a schedule is triggered by the calendar or by a meter, never both; make a second schedule for the other';
    return null;
  }
  return 'a schedule is triggered by Calendar or Meter';
}

export interface ChecklistOutcome {
  done: number;
  total: number;
}

/**
 * A job's checklist outcome for its Service History row (decision 2560): the
 * items marked Done out of every item not dismissed, over every checklist
 * tagged to the Work Order. Null where the job carries no item at all.
 */
export function checklistOutcome(items: readonly { status: string }[]): ChecklistOutcome | null {
  const counted = items.filter((item) => item.status !== 'Dismissed');
  if (counted.length === 0) return null;
  return { done: counted.filter((item) => item.status === 'Done').length, total: counted.length };
}
